import { useContext } from "react";
import { WatchQueryFetchPolicy } from "@apollo/client";
import _ from "lodash";
import sortBy from "lodash/sortBy";
import { useTranslation } from "react-i18next";
import { warningSeveritySortOrder } from "common-client/utils/createAndEditPropertyWarnings";
import { useGetPropertyWarningsQuery } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Buttons/Button";
import { EmptyState } from "../Common/EmptyState";
import { ExpandableList } from "../Common/ExpandableList";
import { Body } from "../Common/Typography";
import { MapRefreshContext } from "../Maps/MapRefreshContext";
import { EditableWarning, WarningListItemIcon } from "../Warnings/WarningList";
import { TitleWrapper } from "./__styles__/Warnings";
import { useUpdateWarningsModal } from "./PropertyOverview/UpdateWarningsModal";

export const SEVERITY_TO_COLOR = {
  low: "contentSecondary",
  medium: "contentAttention",
  high: "contentCritical",
} as const;

interface WarningsContainerProps {
  address?: Maybe<string>;
  propertyId: Maybe<string>;
  canEditWarnings: boolean;
  loading: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}
export default ({
  address,
  propertyId,
  canEditWarnings,
  loading: loadingPropertyId,
  fetchPolicy = "cache-and-network",
}: WarningsContainerProps) => {
  const { t } = useTranslation();

  const errorMessage = t("property-warnings-error");
  if (!propertyId && !loadingPropertyId) return <em>{errorMessage}</em>;

  const { refreshMap } = useContext(MapRefreshContext);
  const { account } = useContext(AuthContext);

  const { data, loading, refetch, error } = useGetPropertyWarningsQuery({
    fetchPolicy,
    variables: { propertyId: propertyId! },
    skip: !propertyId,
  });

  const warnings: Array<EditableWarning> =
    account?.accountPropertyWarningDefinitions
      .filter(warning => warning.isActive)
      .map(accountPropertyWarningDefinition => {
        return {
          ...accountPropertyWarningDefinition,
          applies:
            data?.getProperty?.warnings.find(
              warning =>
                warning.accountPropertyWarningDefinitionId ===
                accountPropertyWarningDefinition.id,
            )?.applies ?? false,
        };
      }) ?? [];

  const [showUpdateWarningsModal, hideUpdateWarningsModal] =
    useUpdateWarningsModal({
      onCancel: () => {
        hideUpdateWarningsModal();
      },
      onSubmit: async () => {
        refreshMap(["warnings", "savedViews"]);
        await refetch();
      },
      address,
      propertyId,
      warnings,
    });

  if (loading || loadingPropertyId) return <em>{t("common-loading")}</em>;
  if (error) return <em>{errorMessage}</em>;

  const applicableWarnings = sortBy(
    data?.getProperty?.warnings.filter(propertyWarning =>
      warnings.find(
        accountPropertyWarningDefinition =>
          propertyWarning.accountPropertyWarningDefinitionId ===
          accountPropertyWarningDefinition.id,
      ),
    ),
    ({ severity }) => warningSeveritySortOrder.indexOf(severity),
  ).filter(warning => warning.applies);

  return (
    <>
      <TitleWrapper>
        <Body size="large" type="emphasis">
          {t("property-warnings-header")}
        </Body>

        {canEditWarnings && propertyId && (
          <Button
            variant="outline"
            data-testid="editWarnings"
            onPress={() => showUpdateWarningsModal()}
            size="small"
            title="Edit warnings"
          />
        )}
      </TitleWrapper>
      {applicableWarnings.length > 0 ? (
        <ExpandableList
          extension={16}
          list={applicableWarnings.map((warning, idx) => {
            const showDivider = idx !== applicableWarnings.length - 1;
            return {
              title: warning.title,
              expandableContent: warning.message,
              leftContent: <WarningListItemIcon severity={warning.severity} />,
              showDivider,
            };
          })}
        />
      ) : (
        <>
          <EmptyState
            compact
            title={t("property-warnings-empty-state")}
            paddingTop={0}
            paddingBottom={spacing.l.value}
          />
        </>
      )}
    </>
  );
};
